import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

import { GetStravaDisplayPreferencesQuery } from '../../../../types/__generated__';
import useBasePageQuery from '../../../hooks/useBasePageQuery';
import useFullPageQuery from '../../../hooks/useFullPageQuery';
import { LazyLoader } from '../../LazyLoader/LazyLoader';
import { FitnessMessage } from '../Fitness/FitnessMessage';
import TimelineEntry, {
  TimelineEntryProps,
} from '../TimelineEntry/TimelineEntry';
import { StravaAvatarBadge } from './StravaAvatarBadge';
import getStravaDisplayPreferences from './StravaDisplayPreferences.gql';
import { StravaLink } from './StravaLink';

import styles from './StravaTimelineEntry.scss';
import { Header } from '../TimelineEntry/Header';
import { Body } from '../TimelineEntry/Body';
import { Footer } from '../TimelineEntry/Footer';

// Lazy loading the StravaMap as react-leaflet only works on client side
// and it loads the mapping library and css for leaflet so is only needed
// when rendering a map
const StravaMapLazy = React.lazy(() => import('./StravaMap'));

const stravaOrange = '#fc4c02';

export const StravaTimelineEntry: FunctionComponent<TimelineEntryProps> = ({
  timelineEntry,
  avatarUrl,
  postedBy,
  onDeleteEntry,
}) => {
  const basePageData = useBasePageQuery();
  const { data: settingsData } = useFullPageQuery<
    GetStravaDisplayPreferencesQuery
  >(getStravaDisplayPreferences);
  const settings = settingsData?.page?.strava?.settings ?? {};
  const { t } = useTranslation('common', { useSuspense: false });
  const { fitnessActivity } = timelineEntry;
  const { activityId, mapPolyline } = fitnessActivity!;

  const branding = basePageData?.data?.page?.branding;

  const shouldDisableRefreshButton = () => {
    if (!timelineEntry.refreshRequestedTime) {
      return false;
    }
    const earliestTimeToEnableRefreshButton = new Date(
      timelineEntry.refreshRequestedTime,
    );
    earliestTimeToEnableRefreshButton.setSeconds(
      earliestTimeToEnableRefreshButton.getSeconds() + 1,
    );

    return new Date(Date.now()) < earliestTimeToEnableRefreshButton;
  };

  return (
    <TimelineEntry key={timelineEntry.id} timelineEntry={timelineEntry}>
      <Header
        timelineEntry={timelineEntry}
        avatarUrl={avatarUrl}
        postedBy={
          <>
            <strong>{postedBy}</strong> via <strong>Strava</strong>
          </>
        }
        avatarBadge={<StravaAvatarBadge />}
        onDeleteEntry={onDeleteEntry}
        disableRefreshButton={shouldDisableRefreshButton()}
      />
      <Body
        timelineEntry={timelineEntry}
        showPhotos={settings.showPhotos ?? undefined}
      >
        <FitnessMessage
          message={timelineEntry.message}
          caption={t('strava.timeline.label')}
          fitness={fitnessActivity!}
          settings={settings}
        />
        {mapPolyline && settings.showMap ? (
          <LazyLoader>
            <div className={styles.mapContainer}>
              <StravaMapLazy
                polyline={mapPolyline}
                color={branding?.primaryColor ?? stravaOrange}
              />
            </div>
          </LazyLoader>
        ) : null}
      </Body>
      <Footer timelineEntry={timelineEntry}>
        {activityId && <StravaLink activityId={activityId} />}
      </Footer>
    </TimelineEntry>
  );
};
